/**
 * 图片懒加载指令
 *
 * 使用方式：
 * <img v-lazy="imageUrl" alt="description" />
 *
 * 功能：
 * 1. 使用 Intersection Observer API 实现高性能懒加载
 * 2. 支持占位图片
 * 3. 支持加载失败处理
 * 4. 支持淡入动画效果
 *
 * @module shared/directives
 */

import { App, DirectiveBinding } from "vue";

interface LazyLoadOptions {
  /** 占位图片 URL */
  placeholder?: string;
  /** 加载失败时显示的图片 URL */
  error?: string;
  /** 根元素边距（提前加载的距离） */
  rootMargin?: string;
  /** 触发加载的可见度阈值 */
  threshold?: number;
}

const defaultOptions: LazyLoadOptions = {
  placeholder:
    "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjE4IiBmaWxsPSIjY2NjIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+5Yqg6L295LitLi4uPC90ZXh0Pjwvc3ZnPg==",
  error:
    "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2ZmZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjE4IiBmaWxsPSIjZjU2YzZjIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+5Yqg6L296ZSZ6K+vPC90ZXh0Pjwvc3ZnPg==",
  rootMargin: "50px",
  threshold: 0.01,
};

/**
 * 图片加载完成的淡入动画
 */
const fadeIn = (element: HTMLImageElement) => {
  element.style.opacity = "0";
  element.style.transition = "opacity 0.3s ease-in-out";

  requestAnimationFrame(() => {
    element.style.opacity = "1";
  });
};

/**
 * 加载图片
 */
const loadImage = (
  element: HTMLImageElement,
  src: string,
  options: LazyLoadOptions,
): void => {
  const img = new Image();

  img.onload = () => {
    element.src = src;
    element.classList.add("lazy-loaded");
    element.classList.remove("lazy-loading", "lazy-error");
    fadeIn(element);
  };

  img.onerror = () => {
    if (options.error) {
      element.src = options.error;
    }
    element.classList.add("lazy-error");
    element.classList.remove("lazy-loading");
  };

  img.src = src;
};

/**
 * 创建 Intersection Observer
 */
const createObserver = (options: LazyLoadOptions): IntersectionObserver => {
  return new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          const src = img.dataset.lazySrc;

          if (src) {
            img.classList.add("lazy-loading");
            loadImage(img, src, options);
            observer.unobserve(img);
          }
        }
      });
    },
    {
      rootMargin: options.rootMargin,
      threshold: options.threshold,
    },
  );
};

// 全局 Observer 实例
let observer: IntersectionObserver;

/**
 * 懒加载指令
 */
export const lazyLoad = {
  mounted(
    el: HTMLImageElement,
    binding: DirectiveBinding<
      string | { src: string; options?: LazyLoadOptions }
    >,
  ) {
    // 解析绑定值
    let src: string;
    let options: LazyLoadOptions = defaultOptions;

    if (typeof binding.value === "string") {
      src = binding.value;
    } else if (binding.value && typeof binding.value === "object") {
      src = binding.value.src;
      options = { ...defaultOptions, ...binding.value.options };
    } else {
      console.warn("[v-lazy] Invalid binding value:", binding.value);
      return;
    }

    // 设置占位图
    if (options.placeholder) {
      el.src = options.placeholder;
    }

    // 保存真实图片地址
    el.dataset.lazySrc = src;

    // 添加初始类名
    el.classList.add("lazy-image");

    // 创建 Observer（如果还没有创建）
    if (!observer) {
      observer = createObserver(options);
    }

    // 开始观察
    observer.observe(el);
  },

  updated(
    el: HTMLImageElement,
    binding: DirectiveBinding<
      string | { src: string; options?: LazyLoadOptions }
    >,
  ) {
    // 如果 src 变化，重新加载
    let newSrc: string;

    if (typeof binding.value === "string") {
      newSrc = binding.value;
    } else if (binding.value && typeof binding.value === "object") {
      newSrc = binding.value.src;
    } else {
      return;
    }

    if (el.dataset.lazySrc !== newSrc) {
      el.dataset.lazySrc = newSrc;

      // 如果已经加载过，直接加载新图片
      if (el.classList.contains("lazy-loaded")) {
        const options = defaultOptions;
        if (typeof binding.value === "object" && binding.value.options) {
          Object.assign(options, binding.value.options);
        }
        loadImage(el, newSrc, options);
      }
    }
  },

  unmounted(el: HTMLImageElement) {
    // 停止观察
    if (observer) {
      observer.unobserve(el);
    }
  },
};

/**
 * 懒加载指令类型
 */
export type { LazyLoadOptions };

/**
 * 注册全局指令
 */
export default {
  install(app: App) {
    app.directive("lazy", lazyLoad);
  },
};
